import { AxiosInstance } from 'axios';

import { ActionTypes } from '../../../Common/Constants/ActionTypes.Constants';
import { EventGroupType } from '../../../Common/Constants/EventGroupType.Constants';
import { Constants } from '../../../Components/V1/Constants';
import { ServiceRequest as ServiceRequestV1 } from '../../../Components/V1/Models/ServiceRequest';
import { ServiceRequestAttachmentsInfo } from '../../../Components/V1/Models/ServiceRequestAttachmentsInfo';
import { SelfHelpSolution } from '../../../Components/V1/Models/Solution';
import { RecommendedTopics } from '../../../Components/V1/Models/SupportTopic';
import { TemplateApiResponse } from '../../../Components/V1/Template';
import { Helper } from '../../../Components/V1/Utilities/Helper';
import { ServiceRequestParameters } from '../../../Components/Workspace/Dependency Helpers/ServiceRequestParameters';
import { QueryStringParserService } from '../../Services.Secondary';
import { AppSessionContext } from '../SessionContext/AppSessionContext';
import { BaseSupportService } from './BaseSupportService';
import HttpRequestService from './HttpRequestService';
import { HttpRequestServiceSettings } from './HttpRequestServiceSettings';
import BookingAppointment from './Models/BookingAppointment';
import { HttpResponse, ResourceCollection } from './Models/HttpServiceModels';
import { ServiceHealthCDNResponse } from './Models/ServiceHealthCDNResponse';
import { ServiceIncidentFilter, ServiceIncidentList } from './Models/ServiceIncident';
import { ServiceRequest } from './Models/ServiceRequest';
import { SupportTopicCDNResponse } from './Models/SupportTopicCDNResponse';
import type { ApolloItemResponse } from '@elixir/customer-care';

const Constant = Constants.getInstance();
const sessionContext = AppSessionContext.getInstance();

export class PartnerSupportService extends BaseSupportService {
  protected static instance: PartnerSupportService;
  private httpRequestService: HttpRequestService;
  private supportResource: AxiosInstance;
  // Disabled Flights and IsCorpNet headers.
  private supportPartnerResource: AxiosInstance;
  private supportCDNResource: AxiosInstance;
  private supportResourceWithAdminCheck: AxiosInstance;
  private publicFlowResource: AxiosInstance;

  private constructor() {
    super();
    this.httpRequestService = HttpRequestService.getInstance();
    this.supportResource = this.httpRequestService.getClientTokenRequestInstance();
    this.supportCDNResource = this.httpRequestService.getCDNRequestInstance();
    // We don't need flights information and isCorpNet in the header while making a call for
    // support Request. Given that these information can be available at some delay we create a new instance
    // where the requirement for these headers is disabled.
    this.supportPartnerResource = this.httpRequestService.getClientTokenRequestInstance(false, false);
    this.supportResourceWithAdminCheck = this.httpRequestService.getClientTokenRequestInstance(false, false, true);
    this.publicFlowResource = this.httpRequestService.getPublicRequestInstance();
  }

  public static getInstance = () => {
    if (!PartnerSupportService.instance) {
      PartnerSupportService.instance = new PartnerSupportService();
    }
    return PartnerSupportService.instance;
  };

  public getPartnerServiceRequest = async (serviceRequestId: string) => {
    return (await this.retry(this.supportPartnerResource.get, HttpRequestServiceSettings.PartnerSupportServiceEndpoint + '/v1/servicerequests/' + serviceRequestId)) as HttpResponse<ServiceRequest>;
  };

  public getPartnerCustomerServiceRequest = async (customerId: string, serviceRequestId: string) => {
    return (await this.retry(
      this.supportPartnerResource.get,
      HttpRequestServiceSettings.PartnerSupportServiceEndpoint + '/v1/customers/' + customerId + '/servicerequests/' + serviceRequestId
    )) as HttpResponse<ServiceRequest>;
  };

  public getServiceRequestList = async (parameters: ServiceRequestParameters) => {
    let filterString = '';
    if (parameters.filter && parameters.filter !== null) {
      filterString = 'filter=' + JSON.stringify(parameters.filter) + '&';
    }

    return (await this.retry(
      this.supportPartnerResource.get,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '?' + filterString + 'offset=' + parameters.offset + '&size=' + parameters.size
    )) as HttpResponse<ResourceCollection<ServiceRequest>>;
  };

  public getSupportRequestAdminList = async (parameters: ServiceRequestParameters) => {
    let filterString = '';
    if (parameters.filter && parameters.filter !== null) {
      filterString = 'filter=' + JSON.stringify(parameters.filter) + '&';
    }

    return (await this.retry(
      this.supportResourceWithAdminCheck.get,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '?' + filterString + 'offset=' + parameters.offset + '&size=' + parameters.size
    )) as HttpResponse<ResourceCollection<ServiceRequest>>;
  };

  public getServiceRequestAppointment = async (serviceRequestId: string) => {
    this.telemetryService.traceEvent(
      ActionTypes.serviceCallRequestAppointmentGet,
      { ticketId: serviceRequestId },
      EventGroupType.appointments
    );
    return (await this.retry(
      this.supportResource.get,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + serviceRequestId + '/appointment'
    )) as HttpResponse<BookingAppointment>;
  };

  public getServiceRequestAppointmentAdmin = async (serviceRequestId: string) => {
    this.telemetryService.traceEvent(
      ActionTypes.serviceCallRequestAppointmentGet,
      { ticketId: serviceRequestId },
      EventGroupType.appointments
    );
    return (await this.retry(
      this.supportResourceWithAdminCheck.get,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + serviceRequestId + '/appointment'
    )) as HttpResponse<BookingAppointment>;
  };

  public getAvailableServiceRequestAppointments = async () => {
    return (await this.retry(this.supportResource.get, HttpRequestServiceSettings.PartnerApiTrafficManagerEndpoint + '/v1/servicerequests/appointments/available')) as HttpResponse<ServiceRequest[]>;
  };

  public getUpcomingServiceRequestAppointments = async () => {
    return (await this.retry(this.supportResource.get, HttpRequestServiceSettings.PartnerApiTrafficManagerEndpoint + '/v1/servicerequests/appointments/upcoming')) as HttpResponse<ServiceRequest[]>;
  };

  public getSupportTopics = async () => {
    const lowerCaseLocale = this.locale.toLowerCase().replace('-', '_');
    const productFamilyId = '08c1ce1a-b1e0-6acc-d1e6-c2420d3c1fc1';
    return (await this.retry(
      this.supportCDNResource.get,
      HttpRequestServiceSettings.PartnerSupportCDNEnpoint + '/supporttopics/' + productFamilyId + '/' + lowerCaseLocale + '.txt'
    )) as HttpResponse<SupportTopicCDNResponse>;
  };

  public getServiceHealthList = async () => {
    const productFamilyId = '08c1ce1a-b1e0-6acc-d1e6-c2420d3c1fc1';
    return (await this.retry(
      this.supportCDNResource.get,
      HttpRequestServiceSettings.PartnerSupportCDNEnpoint + '/servicehealthlist/' + productFamilyId + '/en-us.txt'
    )) as HttpResponse<ServiceHealthCDNResponse>;
  };

  public getServiceIncidents = async (serviceIncidentFilter?: ServiceIncidentFilter) => {
    let queryString = '?';
    if (serviceIncidentFilter) {
      if (serviceIncidentFilter.serviceId) {
        queryString = queryString + 'serviceId=' + serviceIncidentFilter.serviceId + '&';
      }
      if (serviceIncidentFilter.status) {
        queryString = queryString + 'Status=' + serviceIncidentFilter.status + '&';
      }
      if (serviceIncidentFilter.incidentsFromDateTime) {
        queryString = queryString + 'IncidentStartDateTime=' + serviceIncidentFilter.incidentsFromDateTime;
      }
    }

    //TODO: PASS CONTINUATION TOKEN

    return (await this.retry(
      this.supportPartnerResource.get,
      HttpRequestServiceSettings.PartnerSupportServiceEndpoint + '/v1/serviceincidents' + queryString
    )) as HttpResponse<ServiceIncidentList>;
  };

  public getServiceRequestAttachments = async (serviceRequestId: string, customerOrganizationId: string = '') => {
    return await this.retry(
      this.supportResource.get,
      HttpRequestServiceSettings.PartnerSupportServiceEndpoint + '/v1/servicerequests/' + serviceRequestId + '/attachments' + '?customerOrganizationId=' + customerOrganizationId
    );
  };

  public getServiceRequestAdminAttachments = async (serviceRequestId: string, customerOrganizationId: string = '') => {
    return await this.retry(
      this.supportResourceWithAdminCheck.get,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + serviceRequestId + '/attachments' + '?customerOrganizationId=' + customerOrganizationId
    );
  };

  public getFileByName = async (id: string, filename: string) => {
    return await this.retry(this.supportResource.get, HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + id + '/attachments/data?fileName=' + filename);
  };

  public getAdminFileByName = async (id: string, filename: string) => {
    return await this.retry(this.supportResourceWithAdminCheck.get, HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + id + '/attachments/data?fileName=' + filename);
  };

  public updateServiceRequest = async (service: ServiceRequest) => {
    const updateEndpoint = HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + service.id;
    return await this.retry(this.supportResource.patch, updateEndpoint, service);
  };

  public updateServiceRequestV1 = async (service: ServiceRequestV1) => {
    return await this.retry(
      this.supportResource.patch,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + service.id,
      service
    );
  };

  public updateCustomerServiceRequest = async (service: ServiceRequest, id: string) => {
    return await this.retry(this.supportResource.patch, HttpRequestServiceSettings.PartnerServiceCustomerEndpoint + '/' + id + '/servicerequests/' + service.id, service);
  };

  public updateServiceRequestAdmin = async (service: ServiceRequest) => {
    return await this.retry(this.supportResourceWithAdminCheck.patch, HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + service.id, service);
  };

  public getServiceRequestsDetails = async (ticketId: string, includeEmailInteractions: boolean) => {
    return (await this.retry(
      this.supportPartnerResource.get,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + ticketId + '?includeEmailInteractions=' + includeEmailInteractions
    )) as HttpResponse<ServiceRequest>;
  };

  public getCustomerServiceRequestsDetails = async (id: string, ticketId: string) => {
    return await this.retry(this.supportResource.get, HttpRequestServiceSettings.PartnerServiceCustomerEndpoint + '/' + id + '/servicerequests/' + ticketId);
  };

  public getServiceRequestsDetailsAdmin = async (ticketId: string, includeEmailInteractions: boolean) => {
    return (await this.retry(
      this.supportResourceWithAdminCheck.get,
      HttpRequestServiceSettings.ServiceRequestEndpoint + '/' + ticketId + '?includeEmailInteractions=' + includeEmailInteractions
    )) as HttpResponse<ServiceRequest>;
  };

  // Fetch customer details (companyProfile.companyName)
  public async getCustomerDetails(customerId: string): Promise<HttpResponse<{ companyProfile: { companyName: string } }>> {
    return (await this.retry(this.supportPartnerResource.get, HttpRequestServiceSettings.PartnerApiEndpoint + `/v1/customers/${customerId}`)) as HttpResponse<{
      companyProfile: { companyName: string };
    }>;
  }

  // Fetch GDAP fallback customer details (organizationDisplayName)
  public async getGranularAdminCustomers(customerId: string): Promise<HttpResponse<{ organizationDisplayName: string }>> {
    return (await this.retry(this.supportPartnerResource.get, HttpRequestServiceSettings.PartnerSupportServiceEndpoint + `/v1/granularAdminCustomers/${customerId}`)) as HttpResponse<{
      organizationDisplayName: string;
    }>;
  }

  // Fetch granular admin accesses for a customer (GDAP roles)
  public async getGranularAdminAccesses(customerId: string): Promise<HttpResponse<any>> {
    return (await this.retry(
      this.supportPartnerResource.get,
      HttpRequestServiceSettings.PartnerApiTrafficManagerEndpoint + `/api/granularadminaccesses?customerId=${customerId}`
    )) as HttpResponse<any>;
  }

  // Fetch customer service requests for a given customerId and search input
  public async getCustomerServiceRequests(customerId: string, parameters: ServiceRequestParameters): Promise<HttpResponse<any>> {
    let filterString = '';
    if (parameters.filter && parameters.filter !== null) {
      filterString = 'filter=' + JSON.stringify(parameters.filter) + '&';
    }

    return (await this.retry(
      this.supportPartnerResource.get,
      HttpRequestServiceSettings.PartnerSupportServiceEndpoint + `/v1/customers/${customerId}/servicerequests` + '?' + filterString + 'offset=' + parameters.offset + '&size=' + parameters.size
    )) as HttpResponse<ResourceCollection<ServiceRequest>>;
  }

  // Fetch partner claims (roles, permissions, identifiers, etc.)
  public async getPartnerClaims(): Promise<HttpResponse<any>> {
    return (await this.retry(this.supportPartnerResource.get, HttpRequestServiceSettings.PartnerApiTrafficManagerEndpoint + '/api/partnerclaims')) as HttpResponse<any>;
  }

  // Fetch client session context (isGallatinUser, etc.)
  public async getClientSessionContext(): Promise<any> {
    return await this.retry(this.supportPartnerResource.get, HttpRequestServiceSettings.PartnerApiTrafficManagerEndpoint + '/api/clientsessioncontext');
  }

  public getScopingTemplatesData = async (topicId: string) => {
    const productId = '15960';
    const templatePartialUri = 'templates?productId={0}&topicId={1}&isPreview={2}';
    const previewParameter = QueryStringParserService.constainsQuery(Constant.QueryStringParams.isAzPreview)
      ? Constant.QueryStringParams.isAzPreview
      : Constant.QueryStringParams.isPreview;
    return (await this.retry(
      this.publicFlowResource.get,
      Constant.SupportApimEndpoint +
        '/v1/selfhelp/public/' +
        templatePartialUri
          .replace('{0}', productId)
          .replace('{1}', topicId)
          .replace('{2}', QueryStringParserService.getValue(previewParameter))
    )) as HttpResponse<TemplateApiResponse>;
  };

  public getScopingSolutionsData = async (topicId: string): Promise<HttpResponse<SelfHelpSolution[]>> => {
    const productId = '15960';
    const solutionPartialUri = 'apollosolutions?productId={0}&topicId={1}&isPreview={2}';
    const previewParameter = QueryStringParserService.constainsQuery(Constant.QueryStringParams.isAzPreview)
      ? Constant.QueryStringParams.isAzPreview
      : Constant.QueryStringParams.isPreview;
    const response = (await this.retry(
      this.publicFlowResource.get,
      Constant.SupportApimEndpoint +
        '/v1/selfhelp/public/' +
        solutionPartialUri
          .replace('{0}', productId)
          .replace('{1}', encodeURIComponent(topicId))
          .replace('{2}', QueryStringParserService.getValue(previewParameter))
    )) as HttpResponse<ApolloItemResponse>;

    if (!response.data?.properties) {
      throw new Error('Apollo self-help returned an invalid solution response');
    }

    const { content, sections } = response.data.properties;
    const hasContent = content?.trim() || sections?.some(section => section.content?.trim());
    return { ...response, data: hasContent ? [{ apollo: response.data }] : [] };
  };

  public patchSupportTicket = async (
    id: string = '',
    workspaceId: string,
    xsrfRequestToken: string,
    xsrfCookieToken: string
  ) => {
    const attachmentInfo: ServiceRequestAttachmentsInfo = {
      serviceRequestId: id,
      workspaceId,
    };
    const unauthFlowCreateResource = this.httpRequestService.getUnauthCreateInstance(
      xsrfRequestToken,
      xsrfCookieToken
    );
    return await this.retry(
      unauthFlowCreateResource.patch,
      Constant.SupportApimEndpoint + '/v2/attachments',
      attachmentInfo
    );
  };

  public getRecommendedTopics = async (verbatim: string, productId: string) => {
    const productIdQuery = Helper.isEmptyOrSpaces(productId) ? '' : '&productId=' + productId;
    return (await this.retry(
      this.publicFlowResource.get,
      Constant.SupportApimEndpoint +
        '/v2/supporttopics/public/recommendations?verbatim=' +
        encodeURIComponent(verbatim) +
        '&workflowId=' +
        sessionContext.TicketTrackingId +
        productIdQuery
    )) as HttpResponse<RecommendedTopics[]>;
  };

  public createSupportTicketUnauth = async (
    ticket: ServiceRequestV1,
    agentLocale: string,
    xsrfRequestToken: string,
    xsrfCookieToken: string
  ) => {
    const unauthFlowCreateResource = this.httpRequestService.getUnauthCreateInstance(
      xsrfRequestToken,
      xsrfCookieToken
    );
    return (await this.retry(
      unauthFlowCreateResource.post,
      Constant.SupportApimEndpoint + '/v1/servicerequests/public/' + agentLocale,
      ticket
    )) as HttpResponse<ServiceRequestV1>;
  };

  public getXsrfToken = async () => {
    return (await this.retry(
      this.publicFlowResource.get,
      Constant.SupportApimEndpoint + '/v1/servicerequests/xsrf-token'
    )) as HttpResponse<string>;
  };
}
